import { DomHighlightsRenderer, type HighlightsRenderer } from './highlights-renderer.ts';
import { type GeometryResolver } from './types.ts';

/**
 * Controller-owned resources supplied to one rendering strategy instance.
 */
export interface HighlightsRenderingStrategyContext {
  readonly container: HTMLElement;
  readonly resolver: GeometryResolver;
  invalidateGeometry(): void;
}

/**
 * Coverage policy, independent of feature identity. In V2, body-and-chrome
 * extends row mode into the entry prefix, column mode into headers, and container
 * mode into both and their corner. Cell mode always covers body cells only.
 * Omission defaults to body. Existing strategies ignore this option; physical
 * routing and frozen-boundary fragmentation remain layout-owned for every layer.
 */
export interface HighlightLayerOptions {
  readonly coverage?: 'body' | 'body-and-chrome';
}

/**
 * Controller-level policy for creating logical layers.
 *
 * A strategy may share physical surfaces or layout observation across every
 * layer it creates. Feature code never resolves or calls a strategy directly;
 * application bootstrap selects one and `HighlightsController` owns its
 * lifecycle.
 */
export interface HighlightsRenderingStrategy {
  createLayer(options?: HighlightLayerOptions): HighlightsRenderer;
  dispose(): void;
}

export type CreateHighlightsRenderingStrategy = (
  context: HighlightsRenderingStrategyContext
) => HighlightsRenderingStrategy;

/**
 * Default strategy for layouts where every highlight uses one DOM coordinate
 * space. Each logical layer receives one `DomHighlightsRenderer`.
 */
export function createDomHighlightsRenderingStrategy({
  container,
  resolver
}: HighlightsRenderingStrategyContext): HighlightsRenderingStrategy {
  const layers = new Set<DomHighlightsRenderer>();

  return {
    createLayer() {
      const layer = new DomHighlightsRenderer(container, resolver, () => {
        layers.delete(layer);
      });

      layers.add(layer);

      return layer;
    },

    dispose() {
      for (const layer of [...layers]) {
        layer.dispose();
      }

      layers.clear();
    }
  };
}
